import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from 'helpers/redux';
import { RootState } from 'app/store';
import { CatalogItem, CATALOG_CATEGORY_MAP } from '../models';
import TextArea from 'components/TextArea/TextArea';
import SingleFileDropzone from 'components/FileInput/SingleDropzone';
import Button from 'components/Button/Button';
import { IoMdClose } from 'react-icons/io';
import { RiUploadCloud2Line, RiHistoryLine } from 'react-icons/ri';
import { uploadCatalogVersion } from '../catalog.reducer';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

interface UploadNewVersionModalProps {
    isOpen: boolean;
    onClose: () => void;
    item: CatalogItem;
    onSuccess?: () => void;
}

interface NewVersionForm {
    description: string;
    file: any;
}

const schema = yup.object().shape({
    description: yup
        .string()
        .required('Description / Change summary is required'),
    file: yup.mixed().required('Please attach the new version file'),
});

const UploadNewVersionModal: React.FC<UploadNewVersionModalProps> = ({
    isOpen,
    onClose,
    item,
    onSuccess,
}) => {
    const dispatch = useAppDispatch();
    const MySwal = withReactContent(Swal);

    const isLoading = useAppSelector(
        (state: RootState) => state.catalog.isLoading ?? false,
    );
    const uploadProgress = useAppSelector(
        (state: RootState) => state.catalog.uploadProgress ?? 0,
    );
    const versionHistory = useAppSelector(
        (state: RootState) => state.catalog.versionHistory ?? [],
    );

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors },
    } = useForm<NewVersionForm>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            description: '',
            file: undefined,
        },
    });

    const fileWatcher = watch('file');

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    const onFileChange = (file: File) => {
        setValue('file', file, { shouldValidate: true });
    };

    const onSubmit = async (data: NewVersionForm) => {
        const res = await dispatch(
            uploadCatalogVersion({ id: item.id, form: data }),
        );
        if (uploadCatalogVersion.fulfilled.match(res)) {
            MySwal.fire({
                title: 'New Version Uploaded!',
                text: `"${item.title}" has been updated with a new version and set to Draft (Pending Review).`,
                icon: 'success',
                confirmButtonColor: '#4c8b6a',
            });
            reset();
            if (onSuccess) onSuccess();
            onClose();
        } else {
            MySwal.fire({
                title: 'Upload Failed',
                text: 'There was an error uploading the new version. Please try again.',
                icon: 'error',
            });
        }
    };

    if (!isOpen || !item) return null;

    // Calculate next version display
    const latestHistoryVer =
        versionHistory.length > 0 ? versionHistory[0].version : null;
    const currentVerStr = latestHistoryVer || item.version || 'V1.0';
    const numPart = parseFloat(currentVerStr.replace(/[^0-9.]/g, '')) || 1.0;
    const nextVerStr = `V${(numPart + 0.1).toFixed(1)}`;

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-end bg-black/50 backdrop-blur-sm transition-opacity duration-300">
            <div className="bg-white dark:bg-gray-800 w-full max-w-lg h-full overflow-y-auto shadow-2xl flex flex-col justify-between animate-slide-left border-l border-gray-100 dark:border-gray-700">
                {/* Header */}
                <div>
                    <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur z-10">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                                <RiHistoryLine
                                    className="mr-2 text-[#4c8b6a]"
                                    size={24}
                                />
                                Upload New Version
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-[280px]">
                                {item.title}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <IoMdClose size={22} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="space-y-4"
                        >
                            {/* Read-only Document Title & Category */}
                            <div className="p-4 bg-gray-50 dark:bg-gray-700/40 rounded-xl border border-gray-200 dark:border-gray-600 space-y-2">
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-400">
                                        Document Title:
                                    </span>
                                    <span className="font-bold text-gray-700 dark:text-gray-200">
                                        {item.title}
                                    </span>
                                </div>
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-400">
                                        Category:
                                    </span>
                                    <span className="font-semibold text-[#4c8b6a]">
                                        {CATALOG_CATEGORY_MAP[item.category] ||
                                            'SOP'}
                                    </span>
                                </div>
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-400">
                                        Team:
                                    </span>
                                    <span className="font-semibold text-gray-700 dark:text-gray-200">
                                        {item.team?.name || item.teamId}
                                    </span>
                                </div>
                            </div>

                            {/* Version Transition Box */}
                            <div className="p-4 bg-[#4c8b6a]/10 dark:bg-[#4c8b6a]/20 border border-[#4c8b6a]/30 rounded-xl flex items-center justify-between">
                                <div>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 block">
                                        Current Version
                                    </span>
                                    <span className="text-base font-bold text-gray-700 dark:text-gray-300">
                                        {currentVerStr}
                                    </span>
                                </div>
                                <div className="text-[#4c8b6a] font-bold text-lg">
                                    ➔
                                </div>
                                <div className="text-right">
                                    <span className="text-xs text-[#4c8b6a] font-semibold block">
                                        New Auto Version
                                    </span>
                                    <span className="text-base font-extrabold text-[#4c8b6a]">
                                        {nextVerStr}
                                    </span>
                                </div>
                            </div>

                            {/* Status info */}
                            <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg text-xs text-amber-700 dark:text-amber-300 flex items-center justify-between font-semibold">
                                <span>Status will change to:</span>
                                <span className="flex items-center">
                                    <span className="w-2 h-2 rounded-full bg-amber-500 mr-1.5" />
                                    Pending Review (Needs approval)
                                </span>
                            </div>

                            {/* Description / Change Summary */}
                            <TextArea
                                label="Description / History Perubahan (What changed?) *"
                                placeholder="Explain what was updated or added in this version..."
                                rows={4}
                                {...register('description')}
                                message={errors.description?.message}
                            />

                            {/* File Upload */}
                            <div className="pt-2">
                                <SingleFileDropzone
                                    label="New Version File *"
                                    onChange={onFileChange}
                                    file={fileWatcher as File}
                                    uploadProgress={uploadProgress}
                                    message={errors.file?.message as string}
                                />
                                <p className="text-[11px] text-gray-400 mt-1">
                                    Supported formats: PDF, DOC, DOCX, XLS,
                                    XLSX, PPT, PPTX (Max 50MB)
                                </p>
                            </div>

                            {/* Footer buttons inside form */}
                            <div className="pt-6 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end space-x-3 sticky bottom-0 bg-white dark:bg-gray-800 pb-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="px-5 py-2.5 rounded-lg bg-[#4c8b6a] hover:bg-[#3d7055] text-white font-medium text-sm transition-colors flex items-center justify-center disabled:opacity-50"
                                >
                                    {isLoading
                                        ? 'Uploading...'
                                        : 'Upload New Version'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadNewVersionModal;
